import { useState, useEffect, useCallback, useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { Alert, DataGrid, FilterBar, METRIC_READ_CAPTIONS, MetricCard, MetricCardGrid } from '@aaimplatform/ui';
import type { ActiveFilter, ColumnDef, FilterDefinition } from '@aaimplatform/ui';
import { propertyTitleAdminClient } from '../../api/client';
import { useAuthStore } from '../../stores/auth';
import type { components as PropertyTitleComponents } from '@aaimplatform/institutional-types/property-title-admin';
import {
  Absent,
  EnumBadge,
  GridEmpty,
  GridSkeleton,
  ListHeader,
  TITLE_RISK_LEVEL,
  TITLE_SEARCH_STATUS,
  filterOptions,
  formatUsd,
  formatUsdCompact,
  reportedNumber,
  sumReported,
  tile,
  type ListReadState,
} from './CollateralListParts';

// Canonical types from the deployed property-title-admin service
// (/api/v1/title). This page was migrated 2026-06-19 off the dead
// contract-documentation stub (bare /api/title-searches, which 404s at the
// LB) onto the live canonical client, mirroring the sibling
// collateral/UCCFilings.tsx consumption pattern.
type TitleSearch = PropertyTitleComponents['schemas']['TitleSearch'];
type TitleSearchStatus = PropertyTitleComponents['schemas']['TitleSearchStatus'];

const FILTERS: FilterDefinition[] = [
  { id: 'status', label: 'Status', type: 'select', options: filterOptions(TITLE_SEARCH_STATUS) },
];

function isTitleSearchStatus(v: string | undefined): v is TitleSearchStatus {
  return v != null && Object.prototype.hasOwnProperty.call(TITLE_SEARCH_STATUS, v);
}

function formatAddress(address: TitleSearch['propertyAddress']): {
  primary: string;
  secondary: string;
} | null {
  if (!address?.street) return null;
  const secondaryParts = [address.city, address.state].filter(Boolean).join(', ');
  return {
    primary: address.street,
    secondary: [secondaryParts, address.zipCode].filter(Boolean).join(' '),
  };
}

function RightHeader({ children }: { children: string }) {
  return <span className="block w-full text-right">{children}</span>;
}

/** An absent insurability is not a 'No': without lien data it was never assessed. */
function Insurable({ value }: { value: boolean | null | undefined }) {
  if (value == null) return <Absent />;
  return value ? (
    <span className="text-sm font-sans font-semibold text-growth">Yes</span>
  ) : (
    <span className="text-sm font-sans font-semibold text-error">No</span>
  );
}

/**
 * Title Searches Page -- read-only list over the deployed property-title-admin
 * service (/api/v1/title/searches).
 *
 * Surfaces title risk, lien exposure, and insurability for property
 * collateral. Creating a search answers 422 LIEN_DATA_UNAVAILABLE while ATTOM
 * is the only source, because ATTOM carries no recorded liens (ruling of
 * 2026-09-27), so the list is empty until a title-data source is wired. A
 * figure a row does not report renders as absent, never as a zero that would
 * assert a clean title.
 */
export function TitleSearchesPage() {
  // FI-tenant operators carry a tenantId; platform admins do not, in which
  // case the property-title-admin resolver maps the verified token to the apex
  // tenant (an empty query param is treated as absent server-side).
  const tenantId = useAuthStore((s) => s.user?.tenantId) ?? '';
  const [searches, setSearches] = useState<TitleSearch[]>([]);
  const [read, setRead] = useState<ListReadState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

  const statusValue = activeFilters.find((f) => f.filterId === 'status')?.value;
  const statusFilter = isTitleSearchStatus(statusValue) ? statusValue : undefined;

  const fetchSearches = useCallback(async () => {
    setRead('loading');
    setError(null);
    try {
      const result = await propertyTitleAdminClient.GET('/title/searches', {
        params: {
          query: {
            tenantId,
            status: statusFilter,
            limit: 100,
          },
        },
      });
      if (result.error || !result.data) {
        const status = result.response?.status ?? 0;
        throw new Error(`HTTP ${status}: failed to load title searches`);
      }
      const body = result.data as { searches?: TitleSearch[] };
      setSearches(body.searches ?? []);
      setRead('loaded');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load title searches');
      setRead('failed');
    }
  }, [tenantId, statusFilter]);

  useEffect(() => {
    void fetchSearches();
  }, [fetchSearches]);

  // -------------------------------------------------------------------------
  // Summary metrics. Lien exposure sums only the rows that report a figure.
  // -------------------------------------------------------------------------

  const insurableCount = searches.filter((s) => s.isInsurable === true).length;
  const issuesCount = searches.filter(
    (s) => s.status === 'ISSUES_FOUND' || s.status === 'FAILED'
  ).length;
  const lienExposure = sumReported(searches.map((s) => reportedNumber(s.totalLienAmount)));

  const exposureTile = tile(
    read,
    lienExposure == null ? null : formatUsdCompact(lienExposure),
    searches.length === 0 ? 'No searches on file' : METRIC_READ_CAPTIONS.notReported
  );

  const columns = useMemo<ColumnDef<TitleSearch, unknown>[]>(
    () => [
      {
        id: 'property',
        header: 'Property',
        accessorFn: (s) => s.propertyAddress?.street ?? '',
        cell: ({ row }) => {
          const address = formatAddress(row.original.propertyAddress);
          if (!address) return <Absent />;
          return (
            <div>
              <span className="text-sm font-sans font-medium text-primary leading-normal block">
                {address.primary}
              </span>
              {address.secondary && (
                <span className="text-xs font-sans text-tertiary">{address.secondary}</span>
              )}
            </div>
          );
        },
        size: 260,
      },
      {
        id: 'titleRiskLevel',
        header: 'Title Risk',
        accessorFn: (s) => s.titleRiskLevel ?? '',
        cell: ({ row }) => <EnumBadge value={row.original.titleRiskLevel} map={TITLE_RISK_LEVEL} />,
        size: 120,
      },
      {
        id: 'lienCount',
        header: () => <RightHeader>Lien Count</RightHeader>,
        accessorFn: (s) => reportedNumber(s.lienCount) ?? undefined,
        cell: ({ row }) => {
          const count = reportedNumber(row.original.lienCount);
          return (
            <span className="block text-right text-sm font-mono text-secondary tabular-nums leading-normal">
              {count == null ? <Absent /> : count}
            </span>
          );
        },
        size: 110,
      },
      {
        id: 'totalLienAmount',
        header: () => <RightHeader>Total Lien Amount</RightHeader>,
        accessorFn: (s) => reportedNumber(s.totalLienAmount) ?? undefined,
        cell: ({ row }) => {
          const amount = reportedNumber(row.original.totalLienAmount);
          return (
            <span className="block text-right text-sm font-mono text-secondary tabular-nums leading-normal">
              {amount == null ? <Absent /> : formatUsd(amount)}
            </span>
          );
        },
        size: 160,
      },
      {
        id: 'isInsurable',
        header: 'Insurable',
        accessorFn: (s) => (s.isInsurable == null ? '' : String(s.isInsurable)),
        cell: ({ row }) => <Insurable value={row.original.isInsurable} />,
        size: 100,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (s) => s.status,
        cell: ({ row }) => <EnumBadge value={row.original.status} map={TITLE_SEARCH_STATUS} />,
        size: 140,
      },
      {
        id: 'dataSource',
        header: 'Data Source',
        accessorFn: (s) => s.dataSource ?? '',
        cell: ({ row }) =>
          row.original.dataSource ? (
            <span className="text-sm font-sans text-secondary leading-normal">{row.original.dataSource}</span>
          ) : (
            <Absent />
          ),
        size: 120,
      },
      {
        id: 'searchDate',
        header: 'Search Date',
        accessorFn: (s) => s.searchDate ?? '',
        cell: ({ row }) =>
          row.original.searchDate ? (
            <span className="text-sm font-mono text-secondary tabular-nums leading-normal">
              {new Date(row.original.searchDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          ) : (
            <Absent />
          ),
        size: 130,
      },
    ],
    []
  );

  return (
    <div>
      <ListHeader
        subtitle={
          <p className="text-sm font-sans text-tertiary">
            Property title verification, lien exposure, and insurability tracking
          </p>
        }
        onRefresh={() => void fetchSearches()}
      />

      {error && (
        <Alert type="error" title="Unable to load title searches" className="mb-6">
          {error}
        </Alert>
      )}

      <MetricCardGrid className="mb-6">
        <MetricCard label="Total Searches" {...tile(read, String(searches.length))} />
        <MetricCard label="Insurable" {...tile(read, String(insurableCount))} />
        <MetricCard label="Issues Found" {...tile(read, String(issuesCount))} />
        <MetricCard label="Total Lien Exposure" {...exposureTile} />
      </MetricCardGrid>

      <FilterBar
        filters={FILTERS}
        activeFilters={activeFilters}
        onFiltersChange={setActiveFilters}
        className="rounded-t-sm"
      />

      {read === 'loading' ? (
        <GridSkeleton />
      ) : read === 'failed' ? null : searches.length === 0 ? (
        <GridEmpty
          icon={<MapPin className="h-8 w-8" />}
          title={statusFilter ? 'No title searches match this status' : 'No title searches on file'}
          description={
            statusFilter
              ? 'Clear the status filter to see every title search.'
              : 'Recorded liens need a title-data source. ATTOM, the only property source connected today, supplies none, so a title search cannot run yet.'
          }
        />
      ) : (
        <DataGrid<TitleSearch>
          data={searches}
          columns={columns}
          enablePagination
          defaultPageSize={25}
          enableColumnResize
          hoverable
          striped
          getRowId={(s) => s.id}
          className="rounded-b-sm shadow-sm"
        />
      )}
    </div>
  );
}
